package services.storage.http

import it.neckar.lizergy.model.company.CompanyResolver
import it.neckar.lizergy.model.company.UserResolver
import it.neckar.lizergy.model.company.UsersAndCompanies
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.configuration.quote.QuoteSnapshot
import it.neckar.lizergy.model.price.AvailableProducts
import it.neckar.lizergy.model.price.PriceList
import it.neckar.lizergy.model.price.ProductResolver
import it.neckar.lizergy.model.project.previews.toPreviewQuoteElements
import kotlinx.serialization.Serializable
import serialized.AccountingSerializedConfigurationPreview
import serialized.ModuleLayoutsConfiguration
import serialized.SerializedConfigurationPreview
import serialized.SerializedPhotovoltaicsConfiguration

@Serializable
data class FetchConfiguration(
  val configuration: SerializedPhotovoltaicsConfiguration,
  val moduleLayouts: ModuleLayoutsConfiguration,
  val currentQuoteSnapshot: QuoteSnapshot?,
) {
  fun toConfigurationPreview(): SerializedConfigurationPreview {
    val currentQuoteElements = currentQuoteSnapshot?.quote?.quoteElements?.toPreviewQuoteElements()
    return SerializedConfigurationPreview(
      configurationId = configuration.configurationId,
      label = configuration.label,
      sellingCompany = configuration.sellingCompany,
      location = configuration.location,
      editor = configuration.editor,
      powerUsageScenario = configuration.powerUsageScenario,
      einspeiseart = configuration.einspeiseart,
      modulesReport = moduleLayouts.modulesReport,
      inverterSelection = configuration.facilityConfiguration.inverterSelection,
      batteryConfigurationId = configuration.facilityConfiguration.batteryConfigurationId,
      wallboxSelection = configuration.wallboxSelection,
      eigenmontage = configuration.assemblyConfiguration.assemblyStatus.isBeingSelfAssembled,
      quoteElements = currentQuoteElements,
      manualQuoteElements = configuration.manualQuoteElements,
      earningsDistribution = configuration.earningsDistribution,
      worstProblem = configuration.worstProblem,
      formattedProblems = configuration.formattedProblems,
      processStateEditHistory = configuration.processState,
    )
  }

  fun toAccountingConfigurationPreview(
    availableProducts: AvailableProducts,
    productResolver: ProductResolver,
    priceList: PriceList,
  ): AccountingSerializedConfigurationPreview {
    val currentQuoteElements = currentQuoteSnapshot?.quote?.quoteElements?.toPreviewQuoteElements()
    val upToDateQuoteElements = configuration.evaluate(
      moduleLayouts = moduleLayouts,
      currentQuoteSnapshot = currentQuoteSnapshot,
      productResolver = productResolver,
      availableProducts = availableProducts,
      priceList = priceList,
      userResolver = UsersAndCompanies.Companion.empty,
      companyResolver = UsersAndCompanies.Companion.empty,
    ).calculatedQuoteElements.toPreviewQuoteElements()

    return AccountingSerializedConfigurationPreview(
      configurationId = configuration.configurationId,
      label = configuration.label,
      sellingCompany = configuration.sellingCompany,
      location = configuration.location,
      editor = configuration.editor,
      powerUsageScenario = configuration.powerUsageScenario,
      einspeiseart = configuration.einspeiseart,
      modulesReport = moduleLayouts.modulesReport,
      inverterSelection = configuration.facilityConfiguration.inverterSelection,
      batteryConfigurationId = configuration.facilityConfiguration.batteryConfigurationId,
      wallboxSelection = configuration.wallboxSelection,
      eigenmontage = configuration.assemblyConfiguration.assemblyStatus.isBeingSelfAssembled,
      quoteElements = currentQuoteElements,
      upToDateQuoteElements = upToDateQuoteElements,
      manualQuoteElements = configuration.manualQuoteElements,
      earningsDistribution = configuration.earningsDistribution,
      worstProblem = configuration.worstProblem,
      formattedProblems = configuration.formattedProblems,
      processStateEditHistory = configuration.processState,
    )
  }

  fun toQuoteConfiguration(availableProducts: AvailableProducts, productResolver: ProductResolver, priceList: PriceList, userResolver: UserResolver, companyResolver: CompanyResolver): QuoteConfiguration {
    return configuration.evaluate(
      moduleLayouts = moduleLayouts,
      currentQuoteSnapshot = currentQuoteSnapshot,
      productResolver = productResolver,
      availableProducts = availableProducts,
      priceList = priceList,
      userResolver = userResolver,
      companyResolver = companyResolver,
    )
  }

}
